/**
 * Vector BASEMAP for the OpenSeaMap provider — a small, light land/water/road base
 * authored over MapTiler's OpenMapTiles v3 VECTOR tiles. Vector tiles are tiny,
 * GPU-rendered and sharp at any zoom, so this replaces the old streaming raster
 * PNG base: far less bandwidth and main-thread work, the single biggest win for
 * pan/zoom smoothness on the tiles provider.
 *
 * The fills and lines need no vendor sprite or glyphs. Place/road LABELS are
 * optional: pass a `glyphs` URL and we call map.setGlyphs and add a couple of quiet
 * label layers so towns and roads are named. Labels stay tasteful so they don't
 * fight the magenta seamarks.
 *
 * Ordering: every layer id is prefixed `mtbase-`, and the source is `mtbase-src`.
 * seamarkOverlay.firstNonBaseLayerId treats `mtbase-*` as BASE, so the seamark
 * raster and all our app overlays (ownship, ais, route, marks, objects, pilotage)
 * always sit above the whole vector base — labels included. Inserted at the bottom
 * of the stack, just above the map background and below the (hidden) vector cells.
 */
import type { Map as MaplibreMap, LayerSpecification } from 'maplibre-gl';

const SRC = 'mtbase-src';
const PREFIX = 'mtbase-';

/** A clean, light palette that keeps the seamark symbols the loudest thing on screen. */
const C = {
  land: '#f4f1ea',
  water: '#bcd7e6',
  wood: '#e2e9da',
  grass: '#e9efe0',
  ice: '#f4fafb',
  road: '#e4ddcb',
  roadMajor: '#ddd2b3',
  building: '#e7e2d8',
  boundary: '#c3bab0',
  label: '#5a5f66',
  labelHalo: '#f7f5f0',
  roadLabel: '#8a8578',
};

/** Label font stack — Open Sans is what our chart labels use and MapTiler serves it. */
const FONT = ['Open Sans Regular', 'Noto Sans Regular'];

export interface ActiveVectorBasemap {
  /** Vector tile URL template (…/{z}/{x}/{y}.pbf?key=…). */
  url: string;
  /** Source max native zoom (OpenMapTiles v3 = 14); MapLibre overzooms beyond. */
  maxzoom: number;
  /** MapTiler + OSM credit for the base data. */
  attribution: string;
  /**
   * Glyph endpoint (…/fonts/{fontstack}/{range}.pbf?key=…). When given, the map is
   * switched to these glyphs and the place/road label layers are added. Omit for a
   * label-free base that needs no glyphs at all.
   */
  glyphs?: string;
}

/** Fill/line base layers (no glyphs needed), bottom-to-top. */
function baseLayerSpecs(): LayerSpecification[] {
  return [
    // Land fill under everything (the vector source has no land polygon; land is
    // the absence of water, so a full-extent background gives the land colour).
    { id: `${PREFIX}land`, type: 'background', paint: { 'background-color': C.land } },
    {
      id: `${PREFIX}landcover-wood`,
      type: 'fill',
      source: SRC,
      'source-layer': 'landcover',
      filter: ['==', ['get', 'class'], 'wood'],
      paint: { 'fill-color': C.wood, 'fill-opacity': 0.6 },
    },
    {
      id: `${PREFIX}landcover-grass`,
      type: 'fill',
      source: SRC,
      'source-layer': 'landcover',
      filter: ['in', ['get', 'class'], ['literal', ['grass', 'park', 'wetland']]],
      paint: { 'fill-color': C.grass, 'fill-opacity': 0.6 },
    },
    {
      id: `${PREFIX}landcover-ice`,
      type: 'fill',
      source: SRC,
      'source-layer': 'landcover',
      filter: ['in', ['get', 'class'], ['literal', ['ice', 'glacier']]],
      paint: { 'fill-color': C.ice },
    },
    {
      id: `${PREFIX}water`,
      type: 'fill',
      source: SRC,
      'source-layer': 'water',
      paint: { 'fill-color': C.water },
    },
    {
      id: `${PREFIX}waterway`,
      type: 'line',
      source: SRC,
      'source-layer': 'waterway',
      paint: { 'line-color': C.water, 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 1.4] },
    },
    {
      id: `${PREFIX}building`,
      type: 'fill',
      source: SRC,
      'source-layer': 'building',
      minzoom: 14,
      paint: { 'fill-color': C.building, 'fill-opacity': 0.7 },
    },
    {
      id: `${PREFIX}road`,
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      filter: ['in', ['get', 'class'], ['literal', ['secondary', 'tertiary', 'minor', 'service']]],
      minzoom: 11,
      paint: { 'line-color': C.road, 'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.4, 18, 3] },
    },
    {
      id: `${PREFIX}road-major`,
      type: 'line',
      source: SRC,
      'source-layer': 'transportation',
      filter: ['in', ['get', 'class'], ['literal', ['motorway', 'trunk', 'primary']]],
      minzoom: 6,
      paint: { 'line-color': C.roadMajor, 'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.5, 18, 4] },
    },
    {
      id: `${PREFIX}boundary`,
      type: 'line',
      source: SRC,
      'source-layer': 'boundary',
      filter: ['<=', ['get', 'admin_level'], 4],
      paint: { 'line-color': C.boundary, 'line-width': 0.8, 'line-dasharray': [3, 2], 'line-opacity': 0.7 },
    },
  ];
}

/** Prefer a latin/english name so labels stay legible in our UI. */
const NAME_FIELD: unknown = ['coalesce', ['get', 'name:en'], ['get', 'name:latin'], ['get', 'name']];

/** Place/road label layers — only added when a glyphs URL is supplied. */
function labelLayerSpecs(): LayerSpecification[] {
  return [
    {
      id: `${PREFIX}road-label`,
      type: 'symbol',
      source: SRC,
      'source-layer': 'transportation_name',
      minzoom: 13,
      layout: {
        'symbol-placement': 'line',
        'text-field': NAME_FIELD,
        'text-font': FONT,
        'text-size': 10,
      } as LayerSpecification['layout'],
      paint: { 'text-color': C.roadLabel, 'text-halo-color': C.labelHalo, 'text-halo-width': 1.1 },
    },
    {
      id: `${PREFIX}place-village`,
      type: 'symbol',
      source: SRC,
      'source-layer': 'place',
      filter: ['in', ['get', 'class'], ['literal', ['village', 'hamlet', 'suburb', 'neighbourhood']]],
      minzoom: 11,
      layout: {
        'text-field': NAME_FIELD,
        'text-font': FONT,
        'text-size': 11,
        'text-max-width': 7,
        'text-optional': true,
      } as LayerSpecification['layout'],
      paint: { 'text-color': C.label, 'text-halo-color': C.labelHalo, 'text-halo-width': 1.3 },
    },
    {
      id: `${PREFIX}place-town`,
      type: 'symbol',
      source: SRC,
      'source-layer': 'place',
      filter: ['in', ['get', 'class'], ['literal', ['city', 'town']]],
      minzoom: 6,
      layout: {
        'text-field': NAME_FIELD,
        'text-font': FONT,
        'text-size': ['interpolate', ['linear'], ['zoom'], 6, 11, 12, 15],
        'text-max-width': 8,
      } as LayerSpecification['layout'],
      paint: { 'text-color': C.label, 'text-halo-color': C.labelHalo, 'text-halo-width': 1.4 },
    },
  ];
}

/** Every layer this base may add, so remove/re-seat cover fills, lines AND labels. */
function allLayerSpecs(withLabels: boolean): LayerSpecification[] {
  return withLabels ? [...baseLayerSpecs(), ...labelLayerSpecs()] : baseLayerSpecs();
}

/**
 * The id of the first NON-base layer, so the whole mtbase group inserts beneath the
 * vector cells and app overlays. Base = the map background and any existing mtbase
 * layers; everything else is content that must stay on top. Returns undefined when
 * only base layers exist (append to top, still above the background).
 */
export function firstContentLayerId(map: MaplibreMap): string | undefined {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (l.id !== 'background' && !l.id.startsWith(PREFIX)) return l.id;
  }
  return undefined;
}

let currentSig: string | null = null;
let glyphsApplied = false;

/**
 * Set the map glyphs without ever throwing. map.setGlyphs calls Style._checkLoaded
 * internally, which throws "Style is not done loading" if it runs before the style
 * has finished loading — and our chart-source apply can fire that early during
 * init. So try now; if the style isn't ready, defer to the 'load' event. A glyphs
 * hiccup must never blank the whole map.
 */
function safeSetGlyphs(map: MaplibreMap, url: string): void {
  try {
    map.setGlyphs(url);
    glyphsApplied = true;
  } catch {
    map.once('load', () => {
      if (glyphsApplied) return;
      try {
        map.setGlyphs(url);
        glyphsApplied = true;
      } catch {
        /* give up on labels rather than risk the map */
      }
    });
  }
}

/** Remove every mtbase layer (fills, lines, labels) and the source. */
function removeAll(map: MaplibreMap): void {
  for (const spec of allLayerSpecs(true)) if (map.getLayer(spec.id)) map.removeLayer(spec.id);
  if (map.getSource(SRC)) map.removeSource(SRC);
}

/** Show the vector basemap (replacing any current one), or clear it with null. */
export function setVectorBasemap(map: MaplibreMap, base: ActiveVectorBasemap | null): void {
  if (!base) {
    removeAll(map);
    currentSig = null;
    return;
  }
  const withLabels = !!base.glyphs;
  // Switch the map to the label glyphs once (MapTiler serves our Open Sans, so chart
  // text keeps rendering). Guarded so we don't reload glyphs on every re-apply.
  if (withLabels && !glyphsApplied) {
    safeSetGlyphs(map, base.glyphs as string);
  }
  const sig = `${base.url}|${base.maxzoom}|${withLabels ? 'labels' : 'plain'}`;
  const specs = allLayerSpecs(withLabels);
  // Same source already up: nothing to refetch, just re-seat the group beneath the
  // current content (a provider swap may have added layers above it).
  if (currentSig === sig && map.getSource(SRC)) {
    const before = firstContentLayerId(map);
    for (const spec of specs) if (map.getLayer(spec.id)) map.moveLayer(spec.id, before);
    return;
  }
  removeAll(map);
  map.addSource(SRC, {
    type: 'vector',
    tiles: [base.url],
    maxzoom: base.maxzoom,
    attribution: base.attribution,
  });
  const before = firstContentLayerId(map);
  for (const spec of specs) map.addLayer(spec, before);
  currentSig = sig;
}
